import { useCallback, useEffect, useRef, useState } from 'react';
import {
  anchorPlacement, isHighlightAnchor, type HighlightAnchor,
} from '../../core/page-geometry';
import { call, type CommentView, type GetToken, type Thread } from '../api';
import type { DrawPassage } from '../pdf-snapshot';
import { whenExactly, type FetchState } from './MaterialList';

/**
 * The conversation about one material (R16, R17, R18).
 *
 * Three rules from the Worker are rendered here rather than re-decided:
 *
 * 1. **A deleted comment is a tombstone, not a gap.** The row survives so its replies keep
 *    their parent, and the thread says so in place instead of quietly losing a turn in the
 *    conversation.
 * 2. **A deleted material keeps its thread**, under a "Material deleted" header (R14). The
 *    comment box closes with it — the Worker refuses a post to a deleted material, and a box
 *    that accepts words it cannot deliver is worse than no box.
 * 3. **A departed author is "Former member"** (R18, AE6). That string arrives from the
 *    Worker, which resolves it from the group's own roster. This component never looks up an
 *    author and never re-derives the label.
 *
 * ## A comment may be about a passage
 *
 * A teacher selects a sentence in the document and the box shows what they selected, so it is
 * never a guess what a comment is attached to. Posting stores the anchor with the comment;
 * abandoning the selection stores nothing. A comment about the whole material — still the
 * ordinary case — is exactly what it was.
 *
 * Two rules the anchored case adds:
 *
 * 4. **A reply never carries one.** It belongs to the thread its parent anchored, and the
 *    Worker refuses one that tries, so the reply form has no passage and cannot acquire one.
 * 5. **An anchor that cannot be placed still renders, and says so.** A page past the end of
 *    the document, or rects that no longer describe one, and the comment appears with its
 *    quoted passage and a plain statement that it is no longer attached. The alternative —
 *    drawing it on page one, or dropping the comment — is either a confident lie about where a
 *    colleague was looking, or losing what they said. `anchorPlacement` makes that a decision
 *    with three answers rather than two, because "the document is not open yet" must not read
 *    as "it is not there".
 *
 * `collab-comment-box` is a replay block selector. Comments are teachers' words about each
 * other's work; a recording of them is the thing this product would least survive leaking.
 */

export interface CommentThreadProps {
  materialId: string;
  getToken: GetToken;
  /** The signed-in teacher, for the author-may-delete-their-own rule. */
  viewerUserId: string | null;
  /** Whether the API said this teacher is an admin of the group. Never a local guess. */
  isAdmin: boolean;
  /** A 404 on an open session means the membership went away underneath it (R5, AE4). */
  onAccessLost: () => void;
  /** The passage the teacher has selected in the document, if any. */
  pendingAnchor?: HighlightAnchor | null;
  /** The selection was posted, or dropped. */
  onAnchorSettled?: () => void;
  /** What the thread now holds, so the viewer can draw its anchors. */
  onComments?: (comments: CommentView[]) => void;
  /** Which comment is in focus — clicked here, or its highlight clicked over there. */
  focusedCommentId?: string | null;
  /** A comment was clicked. The page scrolls the document to its passage. */
  onFocusComment?: (commentId: string) => void;
  /** How many pages the document has, or null while it is still opening. */
  pageCount?: number | null;
  /** Paints a passage from the open document. Null for an image, or a PDF still opening. */
  drawPassage?: DrawPassage | null;
}

/**
 * The passage a comment hangs from, as the page shows it.
 *
 * The quote is what the browser's selection said the words were, and for a PDF whose font has
 * no map back to letters that is glyph numbers — "+RZ PDQ|" for "How many". The page's pixels
 * under the highlight are right whatever the font did, so once the document is open the passage
 * is painted from it. Until it is, and if the paint fails, the quote is shown as it always was.
 *
 * The canvas is labelled with the quote. For a broken font that label is as wrong as the text
 * was, and no worse; for every other PDF it is the passage, read aloud.
 */
export function PassageSnapshot({ anchor, draw, className }: {
  anchor: HighlightAnchor;
  draw: DrawPassage | null;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [painted, setPainted] = useState<'pending' | 'yes' | 'no'>('pending');
  const { page, quote } = anchor;
  // By value: every reload of the thread is a new array, and a repaint per reload is a flicker.
  const rects = JSON.stringify(anchor.rects);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (draw === null || canvas === null) { setPainted('no'); return; }
    let live = true;
    setPainted('pending');
    void draw({ page, quote, rects: JSON.parse(rects) as HighlightAnchor['rects'] }, canvas)
      .then((ok) => { if (live) setPainted(ok ? 'yes' : 'no'); });
    return () => { live = false; };
  }, [draw, page, quote, rects]);

  return (
    <>
      {draw !== null && painted !== 'no' && (
        <canvas
          ref={canvasRef}
          className="comment-quote-snapshot"
          role="img"
          aria-label={quote}
          hidden={painted !== 'yes'}
        />
      )}
      {(draw === null || painted === 'no') && <span className={className}>{quote}</span>}
    </>
  );
}

interface Node {
  comment: CommentView;
  replies: CommentView[];
}

/** Roots oldest first, each with its replies in the order the Worker returned them. */
export function toThreadTree(comments: CommentView[]): Node[] {
  const roots = comments.filter((comment) => comment.parentId === null);
  return roots.map((comment) => ({
    comment,
    replies: comments.filter((reply) => reply.parentId === comment.id),
  }));
}

export default function CommentThread({
  materialId, getToken, viewerUserId, isAdmin, onAccessLost,
  pendingAnchor = null, onAnchorSettled, onComments,
  focusedCommentId = null, onFocusComment, pageCount = null, drawPassage = null,
}: CommentThreadProps) {
  const [thread, setThread] = useState<Thread | null>(null);
  const [state, setState] = useState<FetchState>('loading');
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Held rather than depended on: `MaterialPage` passes inline arrows. */
  const handlers = useRef({ onComments, onAnchorSettled, onFocusComment });
  handlers.current = { onComments, onAnchorSettled, onFocusComment };

  const load = useCallback(async () => {
    setState('loading');
    const result = await call<Thread>(getToken, `/api/materials/${encodeURIComponent(materialId)}/comments`);
    if (!result.ok) {
      if (result.status === 404) onAccessLost();
      setState('failed');
      return;
    }
    setThread(result.data);
    setState('ready');
  }, [getToken, materialId, onAccessLost]);

  useEffect(() => { void load(); }, [load]);

  // What the viewer draws comes from here, so the two never disagree about which comment an
  // anchor belongs to.
  useEffect(() => {
    handlers.current.onComments?.(thread?.comments ?? []);
  }, [thread]);

  /** The comment in focus is scrolled to, because a thread is longer than its panel. */
  const focusedRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focusedCommentId === null) return;
    focusedRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [focusedCommentId]);

  async function post(body: string, parentId: string | null) {
    if (body.trim().length === 0) return;
    setBusy(true);
    setError(null);
    // A reply carries no anchor, ever: it belongs to the thread its parent anchored, and the
    // Worker refuses one that tries.
    const anchor = parentId === null ? pendingAnchor : null;
    const result = await call<{ comment: CommentView }>(
      getToken,
      `/api/materials/${encodeURIComponent(materialId)}/comments`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(anchor === null ? { body, parentId } : { body, parentId, anchor }),
      },
    );
    setBusy(false);

    if (!result.ok) {
      if (result.status === 404) { onAccessLost(); return; }
      setError('That comment was not posted. Try again in a moment.');
      return;
    }

    setDraft('');
    setReplyDraft('');
    setReplyTo(null);
    handlers.current.onAnchorSettled?.();
    await load();
  }

  async function remove(comment: CommentView) {
    setBusy(true);
    setError(null);
    const result = await call(getToken, `/api/comments/${encodeURIComponent(comment.id)}`, { method: 'DELETE' });
    setBusy(false);
    if (!result.ok) {
      if (result.status === 404) { onAccessLost(); return; }
      setError('That comment was not deleted. Try again in a moment.');
      return;
    }
    await load();
  }

  const mayDelete = (comment: CommentView): boolean =>
    !comment.deleted && (isAdmin || (viewerUserId !== null && comment.authorUserId === viewerUserId));

  /**
   * The passage a comment hangs from, in the three states it can be in.
   *
   * `placed` is a way into the document and is a button, because that is what it does.
   * `unplaceable` is the graceful degradation: the words are still shown, and the comment says
   * plainly that it no longer points anywhere rather than pointing somewhere wrong. `unknown`
   * is the document still opening — the quote, and no claim either way, because claiming
   * "detached" for a second and then taking it back is worse than saying nothing.
   */
  const renderQuote = (comment: CommentView) => {
    if (comment.anchor === null || comment.anchor === undefined) return null;
    const placement = anchorPlacement(comment.anchor, pageCount);
    const quote = isHighlightAnchor(comment.anchor) ? comment.anchor.quote : null;

    if (placement === 'placed') {
      return (
        <button
          type="button"
          className="comment-quote"
          onClick={() => handlers.current.onFocusComment?.(comment.id)}
        >
          {isHighlightAnchor(comment.anchor)
            ? <PassageSnapshot anchor={comment.anchor} draw={drawPassage} className="comment-quote-text" />
            : <span className="comment-quote-text">{quote}</span>}
          <span className="comment-quote-cue">Show in the document</span>
        </button>
      );
    }

    if (placement === 'unknown') {
      return <p className="comment-quote comment-quote-quiet">{quote}</p>;
    }

    return (
      <p className="comment-quote comment-quote-detached">
        {quote !== null && <span className="comment-quote-text">{quote}</span>}
        <span className="comment-quote-cue">
          This passage is no longer in the material, so the comment is not attached to it any
          more.
        </span>
      </p>
    );
  };

  const renderComment = (comment: CommentView, isReply: boolean) => (
    <li
      key={comment.id}
      ref={comment.id === focusedCommentId ? focusedRef : undefined}
      // What `MaterialPage` observes to decide which comments have been on screen (KTD7).
      data-comment-id={comment.id}
      data-created-at={comment.createdAt}
      className={
        `${isReply ? 'reply' : 'comment'}${comment.deleted ? ' tombstoned' : ''}`
        + (comment.id === focusedCommentId ? ' focused' : '')
      }
    >
      {comment.deleted ? (
        // The tombstone. It says what happened rather than showing a blank, because a reply
        // hanging under nothing reads as a bug in the thread. It carries no controls at all —
        // a row that looks like a turn but answers nothing is worse than a row that says so.
        <p className="tombstone">Comment deleted</p>
      ) : (
        <>
          {/*
            * Who, and when, on one line and in that order — the shape the study app's grading
            * panel puts above a comment. The address stays its own element: the browser suite
            * asserts on it exactly, and a name glued to a timestamp is no longer that name.
            */}
          <p className="comment-head">
            <span className="author">{comment.author}</span>
            <time className="meta" dateTime={comment.createdAt}>{whenExactly(comment.createdAt)}</time>
          </p>
          {renderQuote(comment)}
          <p className="body">{comment.body}</p>
          <div className="row-actions">
            {!isReply && !thread?.material.deleted && (
              <button type="button" onClick={() => { setReplyTo(comment.id); setReplyDraft(''); }}>
                Reply
              </button>
            )}
            {mayDelete(comment) && (
              <button type="button" className="danger" disabled={busy} onClick={() => void remove(comment)}>
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </li>
  );

  return (
    <section className="panel collab-comment-box" aria-label="Comments">
      {state === 'loading' && <p className="hint" role="status">Loading comments…</p>}

      {state === 'failed' && (
        <div role="alert">
          <p className="error">We could not load this conversation.</p>
          <button type="button" onClick={() => void load()}>Try again</button>
        </div>
      )}

      {state === 'ready' && thread && (
        <>
          {/*
            * "Comments", not the material's title: the viewer beside this panel is already
            * showing the title, and one name printed twice a column apart reads as two
            * different things. The deleted case keeps its own header, because there is no
            * viewer next to it and R14's statement is the whole point of the panel then.
            */}
          <h3>{thread.material.deleted ? 'Material deleted' : 'Comments'}</h3>
          {thread.material.deleted && (
            <p className="hint">
              This material is no longer in the group. The conversation about it stays here.
            </p>
          )}

          {error && <p className="error" role="alert">{error}</p>}

          {thread.comments.length === 0 && (
            <p className="hint" role="status">No comments yet. Say what you would want said about yours.</p>
          )}

          <ul className="thread">
            {toThreadTree(thread.comments).map(({ comment, replies }) => (
              <li key={comment.id}>
                <ul className="turn">
                  {renderComment(comment, false)}
                  {replies.map((reply) => renderComment(reply, true))}
                </ul>
                {replyTo === comment.id && (
                  <form
                    onSubmit={(event) => { event.preventDefault(); void post(replyDraft, comment.id); }}
                  >
                    <label htmlFor={`reply-${comment.id}`}>Reply</label>
                    <textarea
                      id={`reply-${comment.id}`}
                      rows={2}
                      value={replyDraft}
                      onChange={(event) => setReplyDraft(event.target.value)}
                      disabled={busy}
                    />
                    <button type="submit" disabled={busy}>Post reply</button>
                    <button type="button" onClick={() => setReplyTo(null)}>Cancel</button>
                  </form>
                )}
              </li>
            ))}
          </ul>

          {!thread.material.deleted && (
            <form onSubmit={(event) => { event.preventDefault(); void post(draft, null); }}>
              {pendingAnchor !== null && (
                // What is being commented on, in the teacher's own words off the page. Without
                // it, "add a comment" after a selection is a box that may or may not be about
                // the sentence they just dragged across, and there is no way to tell which.
                <div className="pending-anchor">
                  <p className="pending-anchor-label">
                    On page {pendingAnchor.page}:
                  </p>
                  <p className="pending-anchor-quote">
                    <PassageSnapshot anchor={pendingAnchor} draw={drawPassage} />
                  </p>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => handlers.current.onAnchorSettled?.()}
                  >
                    Comment on the whole material instead
                  </button>
                </div>
              )}
              <label htmlFor={`comment-${materialId}`}>Add a comment</label>
              <textarea
                id={`comment-${materialId}`}
                rows={3}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                disabled={busy}
              />
              <button type="submit" disabled={busy || draft.trim().length === 0}>
                {busy ? 'Posting…' : 'Post comment'}
              </button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
